import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { Location } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { AreaService } from "../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { MediaService } from "../../../../domains/media/services/media.service";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { AreaConfigurationService } from "../../../../domains/areas/services/area-configuration.service";
import { ZoneService } from "../../../../domains/zones/services/zone.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { AssertionIssuesService } from "../../../../domains/assertionIssues/services/assertion-issues.service";
import { AssertionService } from "../../../../domains/assertions/services/assertion.service";
import { ComponentService } from "../../../../domains/components/services/component.service";
import { BqInspectionDataService } from "../../../../domains/jobs/services/bq-inspection-data.service";
import { BqInspectionDiscussAndDemonstrateDataService } from "../../../../domains/jobs/services/bq-inspection-discuss-and-demonstrate-data.service";
import { JobIssueVendorService } from "../../../../domains/jobIssues/services/job-issue-vendor.service";
import { VendorService } from "../../../../domains/vendors/services/vendor-service";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { MissingDataCheckOptions, MissingDataService } from "../../../../shared/utils/missing-data-service";

/** BQ8 is already live (unlike BQ1/BQ5) and needs no feature-flag gate. */
@Component({
  selector: "app-bq8-root",
  standalone: true,
  imports: [RouterOutlet, SkeletonModule],
  providers: [
    BqInspectionDiscussAndDemonstrateDataService,
    // Children that inject the base type get this step's instance rather than a second, empty one.
    { provide: BqInspectionDataService, useExisting: BqInspectionDiscussAndDemonstrateDataService }
  ],
  templateUrl: "./bq8-root.component.html",
  styleUrl: "./bq8-root.component.scss"
})
export class Bq8RootComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private jobId = 0;

  constructor(
    private areaConfigurationService: AreaConfigurationService,
    private areasService: AreaService,
    private assertionIssuesService: AssertionIssuesService,
    private assertionsService: AssertionService,
    private componentsService: ComponentService,
    private jobIssueService: JobIssueService,
    private jobService: JobService,
    private location: Location,
    private logger: LoggerService,
    private lotService: LotService,
    private mediaService: MediaService,
    private route: ActivatedRoute,
    private zoneService: ZoneService,
    protected bqInspectionDataService: BqInspectionDiscussAndDemonstrateDataService,
    private vendorService: VendorService,
    private jobIssueVendorService: JobIssueVendorService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private missingDataService: MissingDataService,
    private toastService: UiToastService
  ) { }

  async ngOnInit(): Promise<void> {
    this.jobId = parseInt(this.route.snapshot.paramMap.get("jobId") ?? "0");
    const bqStep = this.route.snapshot.data["bqStep"] as string;

    // Subscribes to the CM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    const coordinator = new BackgroundRefreshCoordinator(
      this.constructionManagerScheduleService.scheduleRefreshed,
      redirectOnMissingData => this.loadJobData(this.jobId, bqStep, redirectOnMissingData),
      this.destroyRef
    );
    this.bqInspectionDataService.isRefreshingInBackground = this.constructionManagerScheduleService.isFetchingSchedule;
    this.bqInspectionDataService.dataUpdatedPulse = coordinator.dataUpdatedPulse;

    await coordinator.runInitialLoad();
  }

  /**
   * Loads (or re-loads) all local data for the given BQ8 job. `redirectOnMissingData` should only be
   * true for the initial load — a background-refresh-triggered reload should never yank the user away
   * from a page they're already interacting with, so it just logs and no-ops instead.
   */
  private async loadJobData(jobId: number, bqStep: string, redirectOnMissingData: boolean): Promise<boolean> {
    const bq8Job = await this.jobService.getJobById(jobId);
    const missingDataOptions = this.buildMissingDataOptions(jobId, redirectOnMissingData, bq8Job?.lotId);

    if (this.missingDataService.isMissingData(bq8Job, missingDataOptions.job)) {
      return false;
    }

    if (this.missingDataService.isMissingData(bqStep !== `BQ${bq8Job.buildQualityStep}` ? null : bqStep, missingDataOptions.bqStepMismatch)) {
      return false;
    }

    const lot = await this.lotService.getLotById(bq8Job.lotId);
    if (this.missingDataService.isMissingData(lot, missingDataOptions.lot)) {
      return false;
    }

    const zones = await this.zoneService.getZonesByLotId(bq8Job.lotId);
    if (this.missingDataService.isMissingData(zones, missingDataOptions.zones)) {
      return false;
    }

    const startTime = performance.now();

    const [areas, jobIssues, assertionIssues, components, assertions] = await Promise.all([
      this.areasService.getAreasByLotId(bq8Job.lotId),
      this.jobIssueService.getJobIssuesForJob(bq8Job.id),
      this.assertionIssuesService.getAssertionIssuesForJob(bq8Job.id),
      this.componentsService.getComponentsForJob(bq8Job.id),
      this.assertionsService.getAssertionsForJob(bq8Job.id),
    ]);

    const areasByBuildQualityStep = areas.filter(area => area.buildQualityStepScope === bq8Job.buildQualityStep);
    const availableRooms = areas.filter(area => !!area.buildQualityStepScope === false);
    const zonesForAreas = zones.filter(zone => areasByBuildQualityStep.some(area => area.zoneId === zone.id));

    const [issueMedia, assertionMedia] = await Promise.all([
      this.mediaService.getMediaForJobIssues([...jobIssues, ...assertionIssues]),
      this.mediaService.getMediaForAssertions(assertions),
    ]);

    const allIssueIdentifiers = [...new Set([...jobIssues, ...assertionIssues].map(issue => issue.identifier))];
    const jobIssueVendors = await this.jobIssueVendorService.getAllActiveVendorsForIssues(allIssueIdentifiers);

    const endTime = performance.now();
    this.logger.info(`[Bq8RootComponent] Time taken to fetch job details from indexedDB: ${endTime - startTime} ms.`);

    firstValueFrom(this.areaConfigurationService.getConfiguration(bq8Job.divisionId))
      .then(areasConfig => {
        this.bqInspectionDataService.areasConfig.set(areasConfig);
      })
      .catch(error => this.logger.error("[Bq8RootComponent] Failed to load area configuration", error));

    this.vendorService.getVendorsForLotId(lot.id).then(vendorData => {
      const vendors = this.vendorService.processVendorData(vendorData);
      this.bqInspectionDataService.vendors.set(vendors);
    })
      .catch(error => this.logger.error("[Bq8RootComponent] Failed to load vendors for lot", error));

    this.bqInspectionDataService.job.set(bq8Job);
    this.bqInspectionDataService.lot.set(lot);
    this.bqInspectionDataService.areas.set(areasByBuildQualityStep);
    this.bqInspectionDataService.availableRooms.set(availableRooms);
    this.bqInspectionDataService.jobIssues.set(jobIssues);
    this.bqInspectionDataService.media.set([...issueMedia, ...assertionMedia]);
    this.bqInspectionDataService.zones.set(zonesForAreas);
    this.bqInspectionDataService.assertionIssues.set(assertionIssues);
    this.bqInspectionDataService.jobIssueVendors.set(jobIssueVendors);
    this.bqInspectionDataService.components.set(components);
    this.bqInspectionDataService.assertions.set(assertions);
    this.bqInspectionDataService.isCcmInspection.set(false);

    // The checklist's areas sync ahead of their components/assertions, so an area list with no
    // checklist content yet means this lot's BQ8 checklist hasn't finished downloading to the device.
    if (areasByBuildQualityStep.length > 0 && this.bqInspectionDataService.checklistGroups().length === 0) {
      this.logger.warn(`[Bq8RootComponent] No checklist content found on device for lot ${bq8Job.lotId} (job ${jobId}). Job will not be marked as started.`);
      if (redirectOnMissingData) {
        this.toastService.warn("This BQ8 hasn't finished downloading. Reconnect and refresh your schedule before starting.");
      }
    } else {
      await this.bqInspectionDataService.startJob();
    }

    return true;
  }

  private buildMissingDataOptions(jobId: number, redirectOnMissingData: boolean, lotId?: number): Record<"job" | "bqStepMismatch" | "lot" | "zones", MissingDataCheckOptions> {
    return {
      job: {
        redirectOnMissingData,
        redirect: { type: "navigate", route: ["/cm/schedule"] },
        errorMessage: "Error loading job data for BQ8. Please try again.",
        logMessage: `Background refresh could not find job ${jobId}`,
        componentTag: "Bq8RootComponent"
      },
      bqStepMismatch: {
        redirectOnMissingData,
        redirect: { type: "navigate", route: ["/cm/schedule"] },
        errorMessage: "The job's build quality step does not match the expected step. Please try again.",
        logMessage: `Background refresh found a mismatched build quality step for job ${jobId}`,
        componentTag: "Bq8RootComponent"
      },
      lot: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related lot data for BQ8. Please try again.",
        logMessage: `Background refresh could not find lot ${lotId}`,
        componentTag: "Bq8RootComponent"
      },
      zones: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related zone data for BQ8. Please try again.",
        logMessage: `Background refresh could not find zones for lot ${lotId}`,
        componentTag: "Bq8RootComponent"
      }
    };
  }
}
